import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Repeat, Receipt, Copy, TrendingUp, AlertCircle } from "lucide-react";

export default function ExpenseAuditPage() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/expense-audit").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-neutral-500" data-testid="ea-loading">Lade…</div>;

  const f = data.findings;
  const s = data.summary;
  const growth = f.cost_growth || {};

  return (
    <div className="space-y-6" data-testid="expense-audit-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Cashflow-Steuerung</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-3">
          <ShieldAlert className="h-7 w-7" /> Kosten-Audit
        </h1>
        <p className="text-sm text-neutral-500 mt-2">
          AgencyCash prüft automatisch deine Kostenstruktur: wiederkehrende Kosten, Software-Abos, Doubletten, ungewöhnliche Ausgaben und fehlende Projekt­zuordnungen.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={<Repeat className="h-4 w-4" />} label="Wiederkehrend" value={s.recurring_count} testid="ea-stat-recurring" />
        <Stat icon={<Receipt className="h-4 w-4" />} label="Software-Abos" value={s.software_sub_count} testid="ea-stat-subs" />
        <Stat icon={<Copy className="h-4 w-4" />} label="Doubletten" value={s.duplicate_tool_count} accent={s.duplicate_tool_count > 0 ? "yellow" : null} testid="ea-stat-dupes" />
        <Stat icon={<AlertCircle className="h-4 w-4" />} label="Ausreißer" value={s.unusual_expense_count} accent={s.unusual_expense_count > 0 ? "yellow" : null} testid="ea-stat-unusual" />
        <Stat icon={<TrendingUp className="h-4 w-4" />} label="Kostenwachstum" value={growth.growth_pct == null ? "—" : `${growth.growth_pct > 0 ? "+" : ""}${growth.growth_pct}%`} accent={growth.severity} testid="ea-stat-growth" />
      </div>

      <Section title="Kostenwachstum dieser Monat vs. Vormonat" testid="ea-section-growth">
        <div className="grid sm:grid-cols-3 gap-4">
          <Mini label={`${growth.last_month_label || "—"} (Netto)`} value={fmtCurrency(growth.last_month_net || 0)} />
          <Mini label={`${growth.this_month_label || "—"} (Netto)`} value={fmtCurrency(growth.this_month_net || 0)} highlight />
          <Mini label="Delta" value={`${(growth.delta_net || 0) > 0 ? "+" : ""}${fmtCurrency(growth.delta_net || 0)}`} severity={growth.severity} />
        </div>
      </Section>

      <Section title={`Wiederkehrende Kosten ohne Fixkosten-Eintrag (${f.recurring.length})`} testid="ea-section-recurring">
        {f.recurring.length === 0 ? <Empty /> : f.recurring.map((r, i) => (
          <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid={`ea-recurring-${i}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-neutral-900">{r.supplier_name}</div>
                <div className="text-sm text-neutral-600">{r.occurrences} Rechnungen in {r.months_count} Monaten — Ø {fmtCurrency(r.avg_net_amount)} netto</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">Gesamt</div>
                <div className="font-display text-lg font-semibold">{fmtCurrency(r.total_net_amount)}</div>
              </div>
            </div>
            <p className="text-sm text-neutral-700 mt-2">💡 {r.suggestion}</p>
          </div>
        ))}
      </Section>

      <Section title={`Doppelte Tools (${f.duplicate_tools.length})`} testid="ea-section-duplicates">
        {f.duplicate_tools.length === 0 ? <Empty /> : f.duplicate_tools.map((d, i) => (
          <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid={`ea-dup-${i}`}>
            <div className="font-medium text-neutral-900">{d.a.name} ⟷ {d.b.name}</div>
            <p className="text-sm text-neutral-700 mt-1">{d.hint}</p>
          </div>
        ))}
      </Section>

      <Section title={`Ungewöhnliche Ausgaben (${f.unusual_expenses.length})`} testid="ea-section-unusual">
        {f.unusual_expenses.length === 0 ? <Empty /> : f.unusual_expenses.map((u, i) => (
          <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid={`ea-unusual-${i}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-neutral-900">{u.supplier_name}</div>
                <div className="text-sm text-neutral-600">{u.cost_category} • {fmtDate(u.invoice_date)}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-lg font-semibold">{fmtCurrency(u.amount_net)}</div>
                <Badge variant="secondary" className="bg-red-100 text-red-700">{u.ratio}× Durchschnitt</Badge>
              </div>
            </div>
          </div>
        ))}
      </Section>

      <Section title={`Ausgaben ohne Projektzuordnung (${f.missing_project.length})`} testid="ea-section-missing">
        {f.missing_project.length === 0 ? <Empty /> : f.missing_project.map((m, i) => (
          <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid={`ea-missing-${i}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-neutral-900">{m.supplier_name}</div>
                <div className="text-sm text-neutral-600">{m.cost_category} • {fmtDate(m.invoice_date)}</div>
              </div>
              <div className="font-display text-lg font-semibold">{fmtCurrency(m.amount_net)}</div>
            </div>
            <p className="text-sm text-neutral-700 mt-2">💡 {m.hint}</p>
          </div>
        ))}
      </Section>

      <Section title={`Software-Abos (${f.software_subs.length})`} testid="ea-section-subs" collapsible>
        {f.software_subs.length === 0 ? <Empty /> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {f.software_subs.map((sub, i) => (
              <div key={`${sub.source}-${sub.id}-${i}`} className="rounded-lg border border-neutral-200 bg-white p-3 flex justify-between items-center" data-testid={`ea-sub-${i}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{sub.name}</div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider">{sub.source === "fixed_cost" ? "Fixkosten" : "Eingangsrechnung"}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-sm font-semibold tabular-nums">{fmtCurrency(sub.monthly_net || sub.amount_net)}</div>
                  <div className="text-[10px] text-neutral-500">/ Monat (netto)</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Stat({ icon, label, value, accent, testid }) {
  const color =
    accent === "red" ? "text-red-700" :
    accent === "yellow" ? "text-amber-700" :
    accent === "green" ? "text-emerald-700" : "text-neutral-900";
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
        {icon} {label}
      </div>
      <div className={`font-display text-2xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Mini({ label, value, highlight, severity }) {
  const color = severity === "red" ? "text-red-700" : severity === "yellow" ? "text-amber-700" : highlight ? "text-neutral-900" : "text-neutral-600";
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className={`font-display text-xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-neutral-500 italic px-1">Keine Auffälligkeiten — alles sauber. ✓</div>;
}
